import React, { useCallback, useMemo } from "react";
import { useState, useEffect } from "react";
import styles from "./RequirementFormPage.module.css";
import { CpnwContainer } from "hooks/useCpnw";
import { Loading } from "@cpnw/ui";
import RequirementTable from "components/RequirementTable";
import { AdminContainer } from "hooks/useAdmin";
import { format, parseISO } from "date-fns";
import PassportCreateRequirements from "components/PassportCreateRequirements";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { StaticRoutes } from "pages/routes";
import { RequirementContainer } from "hooks/useRequirement";
import classNames from "classnames";
import { AccountContainer } from "hooks/useAccount";
import environment from "environment";
import { CanPermissions } from "pages/enums";

export default function RequirementFormPage() {
  const history = useHistory();
  const {
    requirementSelectboxValuesData,
    refetchRequirementSelectboxValues,
    abbreviationsData,
    refetchAbbreviations,
    requirement,
    setRequirement,
    createRequirement,
    updateRequirement,
    refetchAdminRequirements
  } = RequirementContainer.useContainer();

  const { meData, userRoles, hasCanPermissions } = AccountContainer.useContainer();
  const [isSaving, setIsSaving] = useState(false);

  // Check if user has the required roles
  const hasRequiredRoles = useMemo(() => {
    if (!userRoles) return false;
    return userRoles.some((role) =>
      ["EDUCATION", "HEALTHCARE", "CPNW-ADMIN", "CPNW-REVIEWER", "FACULTY"].includes(
        role.toUpperCase()
      )
    );
  }, [userRoles]);

  useEffect(() => {
    if (!hasRequiredRoles || !hasCanPermissions(CanPermissions.CanCoordinate)) {
      window.location.href = environment.homeUrl;
      return;
    }
  }, [hasRequiredRoles]);

  const isNew = requirement == null;

  const enableWaiver = false;
  const showWaiver = enableWaiver && (userRoles.includes("HEALTHCARE") || userRoles.includes("CPNW-ADMIN"));
  const showWeeksBeforePlacement = userRoles.includes("HEALTHCARE")
  const showBusinessUnitsInsteadOfFacilities = userRoles.includes("EDUCATION") || userRoles.includes("FACULTY")
  const [healthOrEducation, setHealthOrEducation] = useState(false);

  // API Call on mount
  useEffect(() => {
    if (meData) {
      const roles = meData.roles.map((i) => {
        return String(i).toLowerCase();
      });
      let ret =
        roles.includes("education")
        || roles.includes("faculty")
        || roles.includes("healthcare");
      setHealthOrEducation(ret);
    }
  }, [meData]);

  useEffect(async () => {
    setIsSaving(true);

    var requests = []
    if (
      !requirementSelectboxValuesData
    ) {
      requests = [
        refetchRequirementSelectboxValues()
      ]
    }

    if (!abbreviationsData) {
      requests.push(refetchAbbreviations())
    }

    await Promise.all(requests)

    setIsSaving(false);
  }, []);


  const selectFields = useMemo(() => {
    if (
      requirementSelectboxValuesData &&
      abbreviationsData
    ) {
      return {
        requiredBy: requirementSelectboxValuesData.Facilities.map((rf) => rf.Name),
        requiredByBusinessUnits: requirementSelectboxValuesData.BusinessUnits.map((rbu) => {
          return {
            schoolId: rbu.facilityId, // add more field
            schoolName: rbu.facilityName, // add more field
            programId: rbu.Id,
            programName: rbu.Name,
          }
        }),
        category: requirementSelectboxValuesData.Categories.map((f) => f.Name).filter((n) => !(n?.toLowerCase() === "elearning" && healthOrEducation)),
        frequency: requirementSelectboxValuesData.Frequencies.map((f) => f.Name),
        annualDate: requirementSelectboxValuesData.AnnualDate,
        appliesTo: requirementSelectboxValuesData.Disciplines.map((rf) => rf.Name),
        disciplines: requirementSelectboxValuesData.Disciplines.map((rf) => rf.Name),
        status: requirementSelectboxValuesData.Statuses.map((rf) => rf.Name),
        subCategories: requirementSelectboxValuesData.SubCategories,
        categories: requirementSelectboxValuesData.Categories,
        abbreviations: abbreviationsData.data,
        appliesToUserTypes: ["Student", "Faculty"]
      };
    }
    return null;
  }, [
    abbreviationsData,
    requirementSelectboxValuesData,
    healthOrEducation
  ]);

  const selectedValues = useMemo(() => {
    if (
      requirement &&
      requirementSelectboxValuesData
    ) {
      return {
        requiredBy: requirementSelectboxValuesData.Facilities
          .filter((rf) => requirement.FacilityIds.includes(rf.Id))
          .map((rf) => rf.Name),
        requiredByBusinessUnits: requirementSelectboxValuesData.BusinessUnits
          .filter((rf) => requirement.BusinessUnitIds.includes(rf.Id))
          .map((rf) => rf),
        category: requirementSelectboxValuesData.Categories.find(
          (rc) => rc.Id == requirement.CategoryId
        )?.Name ?? "",
        frequency: requirementSelectboxValuesData.Frequencies.find((f) => f.Id == requirement.FrequencyId)
          .Name,
        annualDate: requirement.AnnualDate,
        appliesTo: requirementSelectboxValuesData.Disciplines
          .filter((rd) => requirement.DisciplineIds.includes(rd.Id))
          .map((rf) => rf.Name),
        disciplines: requirementSelectboxValuesData.Disciplines
          .filter((rd) => requirement.DisciplineIds.includes(rd.Id))
          .map((rf) => rf.Name),
        status: requirementSelectboxValuesData.Statuses.find(
          (rsd) => rsd.Id == requirement.StatusId
        ).Name,
        subCategory: requirementSelectboxValuesData.SubCategories.find(
          (rsd) => rsd.Id == requirement.SubCategoryId
        )?.Name,
        title: requirement.Name,
        instructions: requirement.Instructions,
        abbr: requirement.Abbreviation,
        files: requirement.Files,
        module: requirement.Module
          ? {
            id: requirement.Module.Id,
            name: requirement.Module.Name,
            pathname: requirement.Module.Pathname,
            abbr: requirement.Module.Abbr,
          }
          : null,
        allowWaiver: requirement.AllowWaiver,
        weeksBeforePlacement: requirement.WeeksBeforePlacement,
        appliesToUserTypes: requirement.AppliesToUserTypes
      };
    }
    return isNew
      ? {
        requiredBy: [],
        requiredByBusinessUnits: [],
        category: "",
        frequency: "",
        appliesTo: [],
        status: "",
        instructions: "",
        title: "",
        abbr: "",
        files: [],
        subCategory: "",
      }
      : null;
  }, [
    requirement,
    requirementSelectboxValuesData
  ]);

  const onSave = useCallback(
    (values) => {
      console.log("onSave ", values);

      const selectedCategory = requirementSelectboxValuesData.Categories.find(
        (category) => category.Name == values.category
      );

      if (!selectedCategory) {
        alert("Please select a category before saving.");
        return;
      }

      const dto = {
        name: values.title,
        instructions: values.instructions,
        abbreviation: values.abbr,
        categoryId: requirementSelectboxValuesData.Categories.find(
          (a) => a.Name == values.category
        ).Id,
        frequencyId: requirementSelectboxValuesData.Frequencies.find((a) => a.Name == values.frequency).Id,
        annualDate: values.annualDate,
        statusId: requirementSelectboxValuesData.Statuses.find((a) => a.Name == values.status)
          .Id,
        requiredByList: requirementSelectboxValuesData.Facilities
          .filter((a) => values.requiredBy.includes(a.Name))
          .map((a) => a.Id),
        requiredByBusinessUnits: requirementSelectboxValuesData.BusinessUnits
          .filter(item => new Set(values.requiredByBusinessUnits.map(item => item.Id)).has(item.Id)).map(item => item.Id),
        appliesToList: requirementSelectboxValuesData.Disciplines
          .filter((a) => values.appliesTo.includes(a.Name))
          .map((a) => a.Id),
        disciplinesList: requirementSelectboxValuesData.Disciplines
          .filter((a) => values.disciplines.includes(a.Name))
          .map((a) => a.Id),
        subCategoryId: requirementSelectboxValuesData.SubCategories.find((a) => a.Name == values.subCategory)?.Id,
        forceRetake: values.forceRetake,
        allowWaiver: values.allowWaiver,
        weeksBeforePlacement: values.weeksBeforePlacement,
        appliesToUserTypes: values.appliesToUserTypes,
        autoApproval: values.autoApproval,
      };

      if (values.newModule) {
        dto.moduleFileName = values.newModule.name;
      }

      setIsSaving(true);

      if (requirement) {
        //update
        dto.files = values.files;

        if (requirement.Module) {
          dto.module = requirement.Module;
        }

        dto.id = requirement.Id;

        updateRequirement(dto, values.newFiles || [], values.newModule)
          .then(() => {
            history.push(StaticRoutes.Requirements);
          })
          .catch((error) => {
            alert("Error:", error.toString());
          })
          .finally(async () => {
            await refetchAdminRequirements()
            setIsSaving(false);
          });
      } else {
        //create
        createRequirement(dto, values.newFiles || [], values.newModule)
          .then(() => {
            history.push(StaticRoutes.Requirements);
          })
          .catch((error) => {
            console.log('error', error)
            alert("Error:", error.toString());
          })
          .finally(async () => {
            await refetchAdminRequirements()
            setIsSaving(false);
          });
      }
      console.log("dto", dto);
    },
    [
      requirement,
      requirementSelectboxValuesData
    ]
  );

  return (
    <div className={styles.container}>
      {selectFields && selectedValues ? (
        <PassportCreateRequirements
          selectFields={selectFields}
          selectedValues={selectedValues}
          textValues={{
            instructions: [
              "Create a short name for the requirement, plus an abbreviation of no more than 5 characters.",
              <br />,
              <br />,
              "Attach any files the user will need to complete this requirement. Files can include documents, images and small videos a user can download.",
            ],
          }}
          isNew={isNew}
          onSave={onSave}
          showWaiver={showWaiver}
          showWeeksBeforePlacement={showWeeksBeforePlacement}
          showBusinessUnitsInsteadOfFacilities={
            showBusinessUnitsInsteadOfFacilities
          }
        />
      ) : null}

      {isSaving && (
        <div className={styles.loading}>
          <Loading />
        </div>
      )}
    </div>
  );
}
